import { escapeHtml } from '../utils/dom.js';
import { renderMarkdown } from '../utils/markdown.js';
import { mapsSearchUrl } from '../utils/maps-url.js';
import { TICKET_STORAGE_LABEL, TICKET_STORAGE_URL } from '../config/ticket-storage.js';
import { sectionLabel } from '../config/roadtrip-sections.js';
import { resolveTripBases } from '../utils/trip-bases.js';
import {
  connectGoogleDrive,
  disconnectDrive,
  getDriveFolderUrl,
  isDriveConfigured,
  isDriveConnected,
  listTicketFiles,
  uploadTicketFile,
} from '../integrations/google-drive.js';
import {
  deleteInstanceFile,
  listInstanceFiles,
  uploadInstanceFile,
} from '../integrations/instance-files.js';
import {
  createCustomTicketItem,
  deleteCustomTicketItem,
  groupFilesByLink,
  listCustomTicketItems,
} from '../instance-tickets.js';
import { canEdit } from '../store.js';
import { toDayKey } from '../utils/day-key.js';
import { openExpenseQuickAdd } from '../utils/expense-quick-add.js';
import { parsePlannedCost } from '../utils/planned-cost.js';
import { bindTripShell, renderTripShell } from './shell.js';
import { formatDateIT } from '../utils/format.js';

function extractUrl(text) {
  const match = String(text ?? '').match(/(https?:\/\/[^\s·]+|[a-z0-9.-]+\.[a-z]{2,})/i);
  if (!match) return null;
  const url = match[1];
  return url.startsWith('http') ? url : `https://${url}`;
}

function renderPhrases(phrases) {
  if (!Array.isArray(phrases) || !phrases.length) return '<p>—</p>';
  const rows = phrases
    .map(
      (p) => `
      <tr>
        <td>${escapeHtml(p.slovene)}</td>
        <td>${escapeHtml(p.italian)}</td>
      </tr>
    `,
    )
    .join('');
  return `
    <table class="phrase-table">
      <thead><tr><th>Sloveno</th><th>Italiano</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

function fileIcon(mimeType) {
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  return '📎';
}

function shortFileName(name, max = 28) {
  const base = String(name ?? 'file');
  if (base.length <= max) return base;
  return `${base.slice(0, max - 1)}…`;
}

function renderAttachmentChips(files, { editable, attachAttrs = '' }) {
  const chips = (files ?? [])
    .map(
      (file) => `
      <span class="ticket-attachment">
        <a class="ticket-attachment__link" href="${escapeHtml(file.url)}" target="_blank" rel="noopener noreferrer" title="${escapeHtml(file.name)}">
          <span aria-hidden="true">${fileIcon(file.mime_type)}</span>
          ${escapeHtml(shortFileName(file.name))}
        </a>
        ${editable ? `<button type="button" class="ticket-attachment__delete" data-instance-file-delete="${escapeHtml(file.id)}" aria-label="Elimina ${escapeHtml(file.name)}">×</button>` : ''}
      </span>
    `,
    )
    .join('');

  const attachBtn = editable && attachAttrs
    ? `<button type="button" class="ticket-link ticket-link--attach" ${attachAttrs}>📎 Allega</button>`
    : '';

  if (!chips && !attachBtn) return '';
  return `<div class="ticket-attachments">${chips}${attachBtn}</div>`;
}

function buildCatalogListHtml(tickets, ctx, byCatalog, editable) {
  if (!Array.isArray(tickets) || !tickets.length) return '<p>—</p>';
  const defaultDay = ctx.days[0] ? toDayKey(ctx.days[0].date) : ctx.trip.date_start;

  const items = tickets
    .map((t, index) => {
      const bookingUrl = t.bookingUrl ?? t.ticketUrl ?? extractUrl(t.note);
      const attachments = renderAttachmentChips(byCatalog.get(index) ?? [], {
        editable,
        attachAttrs: `data-attach-catalog="${index}"`,
      });
      return `
      <li class="ticket-list__item" data-ticket-idx="${index}">
        <div class="ticket-list__head">
          <strong>${escapeHtml(t.name)}</strong>
          <span class="ticket-list__cost">${escapeHtml(t.costInfo)}</span>
        </div>
        ${t.note ? `<p class="ticket-list__note">${escapeHtml(t.note)}</p>` : ''}
        ${attachments}
        <div class="ticket-list__links">
          ${bookingUrl ? `<a href="${escapeHtml(bookingUrl)}" target="_blank" rel="noopener noreferrer" class="ticket-link">Prenota / info</a>` : ''}
          ${editable && t.costInfo ? `<button type="button" class="ticket-link ticket-link--expense" data-expense-ticket data-ticket-idx="${index}">💰 Registra spesa</button>` : ''}
        </div>
      </li>
    `;
    })
    .join('');

  return `<ul class="ticket-list" data-default-day="${escapeHtml(defaultDay)}">${items}</ul>`;
}

function buildCustomListHtml(customItems, byCustom, editable) {
  if (!customItems.length && !editable) {
    return '<p class="ticket-drive__empty">Nessuna voce personalizzata.</p>';
  }

  const rows = customItems
    .map((item) => {
      const attachments = renderAttachmentChips(byCustom.get(item.id) ?? [], {
        editable,
        attachAttrs: `data-attach-custom="${escapeHtml(item.id)}"`,
      });
      return `
      <li class="ticket-list__item ticket-list__item--custom" data-custom-item-id="${escapeHtml(item.id)}">
        <div class="ticket-list__head">
          <strong>${escapeHtml(item.label)}</strong>
          ${editable ? `<button type="button" class="ticket-link ticket-link--danger" data-delete-custom="${escapeHtml(item.id)}">Elimina voce</button>` : ''}
        </div>
        ${item.note ? `<p class="ticket-list__note">${escapeHtml(item.note)}</p>` : ''}
        ${attachments}
      </li>
    `;
    })
    .join('');

  const addForm = editable
    ? `
    <form class="ticket-custom-form" id="custom-ticket-form">
      <input class="field-input" name="label" type="text" placeholder="Es. Parcheggio Fusine, Museo…" required maxlength="120" />
      <input class="field-input" name="note" type="text" placeholder="Nota (opzionale)" maxlength="200" />
      <button type="submit" class="btn-primary btn-ghost--sm">+ Aggiungi voce</button>
    </form>
  `
    : '';

  return `
    <ul class="ticket-list ticket-list--custom">${rows}</ul>
    ${addForm}
  `;
}

function buildUnlinkedHtml(unlinked, editable) {
  if (!unlinked.length) return '';
  const chips = renderAttachmentChips(unlinked, {
    editable,
    attachAttrs: editable ? 'data-attach-unlinked' : '',
  });
  return `
    <section class="ticket-unlinked">
      <h3 class="ticket-catalog__title">Altri documenti</h3>
      <p class="ticket-drive__hint">File non ancora collegati a una voce dell'elenco.</p>
      ${chips}
    </section>
  `;
}

function buildTicketsBoardHtml(tickets, ctx, customItems, grouped) {
  const editable = canEdit();
  const catalog = buildCatalogListHtml(tickets, ctx, grouped.byCatalog, editable);
  const custom = buildCustomListHtml(customItems, grouped.byCustom, editable);
  const unlinked = buildUnlinkedHtml(grouped.unlinked, editable);

  return `
    <h3 class="ticket-catalog__title">Guida prenotazioni</h3>
    ${catalog}
    <h3 class="ticket-catalog__title">Voci personalizzate</h3>
    <p class="ticket-drive__hint">Aggiungi biglietti o documenti non presenti nella guida (parcheggi, musei extra, ecc.).</p>
    ${custom}
    ${unlinked}
  `;
}

function renderTickets(tickets, ctx) {
  const driveConfigured = isDriveConfigured();
  const driveConnected = isDriveConnected();
  const folderUrl = getDriveFolderUrl() ?? TICKET_STORAGE_URL;

  let driveSection = '';
  if (driveConfigured) {
    driveSection = `
      <section class="ticket-drive ticket-drive--legacy" id="ticket-drive-section">
        <div class="ticket-drive__head">
          <h3 class="ticket-drive__title">Google Drive (personale)</h3>
          <div class="ticket-drive__actions">
            ${driveConnected
              ? '<button type="button" class="btn-ghost btn-ghost--sm" id="drive-disconnect-btn">Scollega</button>'
              : '<button type="button" class="btn-primary btn-ghost--sm" id="drive-connect-btn">Collega Google Drive</button>'}
            ${driveConnected && canEdit() ? '<button type="button" class="btn-primary btn-ghost--sm" id="drive-upload-btn">+ Carica</button>' : ''}
            <input type="file" id="drive-upload-input" accept="image/*,application/pdf" hidden multiple />
          </div>
        </div>
        <p class="ticket-drive__status" id="ticket-drive-status">${driveConnected ? 'Caricamento file…' : 'Collega Drive per una cartella personale (opzionale).'}</p>
        <div class="ticket-drive-grid" id="ticket-drive-grid"></div>
        ${folderUrl ? `<a class="ticket-storage-link" href="${escapeHtml(folderUrl)}" target="_blank" rel="noopener noreferrer">Apri cartella Drive</a>` : ''}
      </section>
    `;
  } else if (TICKET_STORAGE_URL) {
    driveSection = `
      <div class="ticket-storage">
        <a class="ticket-storage-link" href="${escapeHtml(TICKET_STORAGE_URL)}" target="_blank" rel="noopener noreferrer">${escapeHtml(TICKET_STORAGE_LABEL)}</a>
      </div>
    `;
  }

  return `
    <section class="ticket-drive" id="tickets-board">
      <p class="ticket-drive__hint">Collega PDF e foto alle voci dell'elenco o crea voci personalizzate. Tutto il gruppo può vedere; solo editor e organizzatore caricano ed eliminano.</p>
      <p class="ticket-drive__status" id="tickets-board-status">Caricamento…</p>
      <div id="tickets-board-body"></div>
      <input type="file" id="ticket-file-input" accept="image/jpeg,image/png,image/webp,application/pdf" hidden multiple />
    </section>
    ${driveSection}
  `;
}

async function refreshTicketsBoard(container, ctx) {
  const body = container.querySelector('#tickets-board-body');
  const status = container.querySelector('#tickets-board-status');
  if (!body || !status) return;

  const instanceId = ctx.instance.id;
  const tickets = ctx.sections?.ticketsSummary ?? [];

  status.textContent = 'Caricamento documenti…';
  try {
    const [files, customItems] = await Promise.all([
      listInstanceFiles(instanceId),
      listCustomTicketItems(instanceId),
    ]);
    const grouped = groupFilesByLink(files);
    body.innerHTML = buildTicketsBoardHtml(tickets, ctx, customItems, grouped);
    const total = files.length;
    const linked = total - grouped.unlinked.length;
    status.textContent = total
      ? `${total} document${total === 1 ? 'o' : 'i'} · ${linked} collegat${linked === 1 ? 'o' : 'i'}`
      : 'Nessun documento ancora — usa 📎 Allega su una voce';
    bindExpenseFromTickets(container, ctx);
  } catch (err) {
    status.textContent = err.message ?? 'Errore caricamento';
    body.innerHTML = '';
  }
}

function bindTicketsBoard(container, ctx) {
  const board = container.querySelector('#tickets-board');
  const fileInput = container.querySelector('#ticket-file-input');
  if (!board || !fileInput) return;

  const instanceId = ctx.instance.id;
  let pendingAttach = null;

  board.addEventListener('click', async (e) => {
    const attachCatalog = e.target.closest('[data-attach-catalog]');
    if (attachCatalog && canEdit()) {
      pendingAttach = { catalogIndex: Number(attachCatalog.dataset.attachCatalog), ticketItemId: null };
      fileInput.click();
      return;
    }

    const attachCustom = e.target.closest('[data-attach-custom]');
    if (attachCustom && canEdit()) {
      pendingAttach = { catalogIndex: null, ticketItemId: attachCustom.dataset.attachCustom };
      fileInput.click();
      return;
    }

    if (e.target.closest('[data-attach-unlinked]') && canEdit()) {
      pendingAttach = { catalogIndex: null, ticketItemId: null };
      fileInput.click();
      return;
    }

    const deleteFileBtn = e.target.closest('[data-instance-file-delete]');
    if (deleteFileBtn && canEdit()) {
      if (!window.confirm('Eliminare questo documento per tutto il gruppo?')) return;
      deleteFileBtn.disabled = true;
      try {
        await deleteInstanceFile(deleteFileBtn.dataset.instanceFileDelete, instanceId);
        await refreshTicketsBoard(container, ctx);
      } catch (err) {
        const status = container.querySelector('#tickets-board-status');
        if (status) status.textContent = err.message ?? 'Eliminazione fallita';
      }
      return;
    }

    const deleteCustomBtn = e.target.closest('[data-delete-custom]');
    if (deleteCustomBtn && canEdit()) {
      if (!window.confirm('Eliminare questa voce? I file resteranno in «Altri documenti».')) return;
      deleteCustomBtn.disabled = true;
      try {
        await deleteCustomTicketItem(instanceId, deleteCustomBtn.dataset.deleteCustom);
        await refreshTicketsBoard(container, ctx);
      } catch (err) {
        const status = container.querySelector('#tickets-board-status');
        if (status) status.textContent = err.message ?? 'Eliminazione fallita';
      }
    }
  });

  board.addEventListener('submit', async (e) => {
    const form = e.target.closest('#custom-ticket-form');
    if (!form || !canEdit()) return;
    e.preventDefault();
    const fd = new FormData(form);
    const label = String(fd.get('label') ?? '');
    const note = String(fd.get('note') ?? '');
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    try {
      await createCustomTicketItem(instanceId, { label, note });
      form.reset();
      await refreshTicketsBoard(container, ctx);
    } catch (err) {
      const status = container.querySelector('#tickets-board-status');
      if (status) status.textContent = err.message ?? 'Impossibile aggiungere la voce';
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  fileInput.addEventListener('change', async () => {
    const files = [...(fileInput.files ?? [])];
    if (!files.length || !canEdit()) return;
    const link = pendingAttach ?? { catalogIndex: null, ticketItemId: null };
    pendingAttach = null;
    const status = container.querySelector('#tickets-board-status');
    if (status) status.textContent = 'Caricamento in corso…';
    try {
      for (const file of files) {
        await uploadInstanceFile(instanceId, file, {
          catalogIndex: link.catalogIndex,
          ticketItemId: link.ticketItemId,
        });
      }
      await refreshTicketsBoard(container, ctx);
    } catch (err) {
      if (status) status.textContent = err.message ?? 'Caricamento fallito';
    } finally {
      fileInput.value = '';
    }
  });

  refreshTicketsBoard(container, ctx);
}

function renderDriveFileCard(file) {
  const date = file.modifiedTime ? formatDateIT(file.modifiedTime.slice(0, 10)) : '';
  const thumb = file.thumbnailLink
    ? `<img class="ticket-drive-card__thumb" src="${escapeHtml(file.thumbnailLink)}" alt="" loading="lazy" />`
    : `<span class="ticket-drive-card__icon" aria-hidden="true">${fileIcon(file.mimeType)}</span>`;
  return `
    <a class="ticket-drive-card" href="${escapeHtml(file.webViewLink)}" target="_blank" rel="noopener noreferrer">
      ${thumb}
      <span class="ticket-drive-card__name">${escapeHtml(file.name)}</span>
      ${date ? `<span class="ticket-drive-card__date">${escapeHtml(date)}</span>` : ''}
    </a>
  `;
}

async function refreshDriveGrid(container) {
  const grid = container.querySelector('#ticket-drive-grid');
  const status = container.querySelector('#ticket-drive-status');
  if (!grid || !isDriveConnected()) return;

  status.textContent = 'Caricamento file…';
  try {
    const files = await listTicketFiles();
    if (!files.length) {
      grid.innerHTML = '<p class="ticket-drive__empty">Nessun file nella cartella. Carica PDF o foto dei biglietti.</p>';
    } else {
      grid.innerHTML = files.map(renderDriveFileCard).join('');
    }
    status.textContent = `${files.length} file in Drive`;
  } catch (err) {
    status.textContent = err.message;
    grid.innerHTML = '';
  }
}

function bindExpenseFromTickets(container, ctx) {
  const tickets = ctx.sections?.ticketsSummary ?? [];
  const defaultDay =
    container.querySelector('.ticket-list')?.dataset.defaultDay ??
    (ctx.days[0] ? toDayKey(ctx.days[0].date) : ctx.trip.date_start);

  container.querySelectorAll('[data-expense-ticket]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!canEdit()) return;
      const ticket = tickets[Number(btn.dataset.ticketIdx)];
      if (!ticket) return;
      const parsed = parsePlannedCost(ticket.costInfo ?? '');
      openExpenseQuickAdd({
        instanceId: ctx.instance.id,
        dayKey: defaultDay,
        label: ticket.name,
        amount: parsed.suggestedAmount,
        category: parsed.category === 'altro' ? 'biglietti' : parsed.category,
        costPreview: parsed.preview,
      });
    });
  });
}

function bindTicketsDrive(container) {
  const section = container.querySelector('#ticket-drive-section');
  if (!section || !isDriveConfigured()) return;

  const connectBtn = container.querySelector('#drive-connect-btn');
  const disconnectBtn = container.querySelector('#drive-disconnect-btn');
  const uploadBtn = container.querySelector('#drive-upload-btn');
  const uploadInput = container.querySelector('#drive-upload-input');
  const status = container.querySelector('#ticket-drive-status');

  connectBtn?.addEventListener('click', async () => {
    connectBtn.disabled = true;
    try {
      await connectGoogleDrive();
      location.hash = location.hash;
    } catch (err) {
      status.textContent = err.message;
    } finally {
      connectBtn.disabled = false;
    }
  });

  disconnectBtn?.addEventListener('click', () => {
    disconnectDrive();
    location.hash = location.hash;
  });

  uploadBtn?.addEventListener('click', () => uploadInput?.click());

  uploadInput?.addEventListener('change', async () => {
    const files = [...(uploadInput.files ?? [])];
    if (!files.length || !canEdit()) return;
    uploadBtn.disabled = true;
    status.textContent = 'Caricamento in corso…';
    try {
      for (const file of files) {
        await uploadTicketFile(file);
      }
      await refreshDriveGrid(container);
    } catch (err) {
      status.textContent = err.message;
    } finally {
      uploadBtn.disabled = false;
      uploadInput.value = '';
    }
  });

  if (isDriveConnected()) {
    refreshDriveGrid(container);
  }
}

function renderWhereToEat(value) {
  if (typeof value === 'string') return renderMarkdown(value);

  const intro = value?.intro ? `<p class="md-p">${escapeHtml(value.intro)}</p>` : '';
  const footer = value?.footer ? `<p class="md-p">${escapeHtml(value.footer)}</p>` : '';
  const places = value?.places ?? [];

  const rows = places
    .map((p) => {
      const mapsUrl = p.mapsUrl ?? mapsSearchUrl(p.mapsQuery ?? `${p.name} ${p.zone} Slovenia`);
      return `
        <tr>
          <td>${escapeHtml(p.zone)}</td>
          <td>${escapeHtml(p.name)}</td>
          <td>${escapeHtml(p.priceType)}</td>
          <td>${escapeHtml(p.note)}</td>
          <td class="eat-table__maps">
            ${mapsUrl ? `<a href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener noreferrer" class="eat-table__link">Maps</a>` : '—'}
          </td>
        </tr>
      `;
    })
    .join('');

  return `
    <div class="md-content">
      ${intro}
      <div class="md-table-wrap">
        <table class="md-table eat-table">
          <thead>
            <tr>
              <th>Zona</th>
              <th>Locale</th>
              <th>Tipo</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${footer}
    </div>
  `;
}

function renderSectionBody(key, sections, ctx) {
  const value = sections?.[key];
  if (key === 'whereToEat') return renderWhereToEat(value);
  if (key === 'slovenePhrases') return renderPhrases(value);
  if (key === 'ticketsSummary') return renderTickets(value, ctx);
  if (!value) return '<p>Contenuto non disponibile.</p>';
  return renderMarkdown(value);
}

export function renderPlanSection(container, ctx, sectionKey) {
  const title = sectionLabel(sectionKey);
  const body = renderSectionBody(sectionKey, ctx.sections, ctx);

  container.innerHTML = renderTripShell({
    trip: ctx.trip,
    tripId: ctx.tripId,
    activeTab: 'sections',
    members: ctx.live.members,
    bases: resolveTripBases(ctx.live),
    activeSection: sectionKey,
    mainHtml: `
      <article class="section-page">
        <h2 class="view-title">${escapeHtml(title)}</h2>
        ${body}
      </article>
    `,
  });
  bindTripShell(container, {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  });

  if (sectionKey === 'ticketsSummary') {
    bindTicketsBoard(container, ctx);
    bindTicketsDrive(container);
  }
}
